import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Tooltip,
    Select,
    MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { LineChart } from '@mui/x-charts';
import axios from 'axios';
import colors from '../../../Utils/colors';
import { getAdInsights, getAdLeads, changeMetaAdStatus, getAdPreview, getUserAccounts } from '../../../Services';
import images from '../../../Utils/images';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SpinnerLoader from '../../../Components/SpinnerLoader';
import { eventsend } from '../../../Config/analyticsFunctions';
import { CiGlobe } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";
import { AiOutlineLike } from "react-icons/ai";
import { FaRegComment } from "react-icons/fa";
import { PiShareFat } from "react-icons/pi";
import { Modal } from '@mui/material';

const AdDetailAnalytics = () => {
    const { adId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const authtoken = useSelector((state) => state.main.auth_token);
    const [statusLoading, setStatusLoading] = useState(false);
    const [adPreviewData, setAdPreviewData] = useState(null);
    const [s3Link, setS3Link] = useState(null);
    const [thumbnailS3Link, setThumbnailS3Link] = useState(null);
    const [mediaType, setMediaType] = useState('Image');
    const [mediaDimensions, setMediaDimensions] = useState({ width: 0, height: 0 });
    const [isMediaLoaded, setIsMediaLoaded] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const passedData = location?.state || {};
    const localdata = useSelector((state) => state.main.localdata);


    const [adData, setAdData] = useState(null);
    const [leadData, setLeadData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [leadTypeFilter, setLeadTypeFilter] = useState('All Lead Types');
    const [daysFilter, setDaysFilter] = useState('30 Days');
    const [paginationCursors, setPaginationCursors] = useState({
        before: "",
        after: "",
        hasPrevious: false // Add this flag to track if there's a previous page
    });
    const [hasMoreLeads, setHasMoreLeads] = useState(false);
    const [isLoadingMoreLeads, setIsLoadingMoreLeads] = useState(false);
    const MARKUP_RATE = 0.1;
    const [userAccounts, setUserAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    // useEffect(() => {
    //     fetchAdInsights(adId);

    // }, [adId, passedData]);

    // After you receive the leads response
    const formatLeadData = (rawLeadData) => {
        if (!rawLeadData || !Array?.isArray(rawLeadData)) return [];

        return rawLeadData?.map((lead) => {
            // Extract name from field_data
            const nameField = lead?.field_data?.find(field => field?.name === 'full_name');
            const name = nameField && nameField?.values[0] ? nameField?.values[0] : 'Unknown';

            // Extract phone from field_data
            const phoneField = lead?.field_data?.find(field => field?.name === 'phone_number');
            const phone = phoneField && phoneField?.values[0] ? phoneField?.values[0] : 'Unknown';

            // Extract email from field_data
            const emailField = lead?.field_data?.find(field => field?.name === 'email');
            const email = emailField && emailField?.values[0] ? emailField?.values[0] : '';

            // Convert timestamp to readable format
            const sentAt = new Date(lead?.created_time)?.toLocaleString();

            // Determine lead type based on data (you might want to customize this logic)
            // For now we'll assign randomly as in your original code
            const leadType = Math.random() > 0.5 ? 'New' : 'Contacted';

            // Add a placeholder for message count (or derive from actual data if available)
            const messages = Math.floor(Math.random() * 5) + 1;

            return {
                id: lead?.id,
                name,
                phone,
                email,
                sentAt,
                leadType,
                messages,
                createdTime: lead?.created_time // Keep original timestamp for chart
            };
        });
    };

    // In your useEffect or wherever you fetch the leads
    useEffect(() => {
        fetchAdInsights(adId);
        fetchLeadData(adId);
        fetchAdPreview(adId);
    }, [adId, passedData]);

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await getUserAccounts(authtoken);
                if (
                    response &&
                    response.flag &&
                    Array.isArray(response.data?.data)
                ) {
                    const accounts = response.data.data;
                    setUserAccounts(accounts);

                    const matchedAccount = accounts.find(
                        (account) => account?.id === localdata?.metaConfig?.leadsPageId
                    );
                    if (matchedAccount) {
                        setSelectedAccountId(matchedAccount.id);
                    } else if (accounts.length > 0) {
                        setSelectedAccountId(accounts[0].id);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch user accounts", error);
            }
        };

        fetchAccounts();
    }, [authtoken, localdata]);


    // First, modify the fetchLeadData function to append rather than replace data:
    const fetchLeadData = async (adId, pagingCursor = "") => {
        try {
            setIsLoadingMoreLeads(true);
            const token = authtoken;

            const params = {
                adId: adId,
                before: pagingCursor?.startsWith("before:") ? pagingCursor?.replace("before:", "") : "",
                after: pagingCursor?.startsWith("after:") ? pagingCursor?.replace("after:", "") : "",
                isChildAdAccount: passedData?.isChildAdAccount || false,
            };

            const leadsResponse = await getAdLeads(params, token);

            if (leadsResponse?.status === 200 && leadsResponse?.flag === true) {
                // Format the leads data
                const formattedLeads = formatLeadData(leadsResponse?.data?.data);

                // Set the leads data
                setLeadData(formattedLeads);

                // Update pagination state
                if (leadsResponse?.data?.paging && leadsResponse?.data?.paging?.cursors) {
                    setPaginationCursors({
                        before: leadsResponse?.data?.paging?.cursors?.before || "",
                        after: leadsResponse?.data?.paging?.cursors?.after || "",
                        hasPrevious: pagingCursor?.startsWith("after:") || !!leadsResponse?.data?.paging?.previous // Enable previous if we've navigated forward
                    });
                    setHasMoreLeads(!!leadsResponse?.data?.paging?.next);
                } else {
                    setPaginationCursors({
                        before: "",
                        after: "",
                        hasPrevious: false
                    });
                    setHasMoreLeads(false);
                }
            }
        } catch (error) {
            console.error('Error fetching leads:', error);
        } finally {
            setIsLoadingMoreLeads(false);
        }
    };
    const calculateAdaptiveHeight = () => {
        if (!isMediaLoaded || !mediaDimensions.width || !mediaDimensions.height) {
            return '200px'; // Default height
        }

        const containerWidth = 300; // Adjust based on your container width
        const aspectRatio = mediaDimensions.height / mediaDimensions.width;
        return `${containerWidth * aspectRatio}px`;
    };
    const handlePreviewClick = () => {
        setPreviewModalOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewModalOpen(false);
    };
    const fetchAdPreview = async (adId) => {
        try {
            const token = authtoken;
            const insightsResponse = await getAdPreview(token, { adId: adId });

            // console.log('insightsResponse', insightsResponse);

            if (insightsResponse?.status === 200 && insightsResponse?.flag === true) {
                const previewData = insightsResponse?.data;
                setAdPreviewData(previewData);

                // Set media URLs
                setS3Link(previewData?.videoUrl ? previewData?.videoUrl : previewData?.imageUrl);
                setThumbnailS3Link(previewData?.thumbnailUrl);
                setMediaType(previewData?.videoUrl ? 'Video' : 'Image');
            }
        } catch (error) {
            console.error('Error fetching ad preview:', error);
        }
    };

    const loadMoreLeads = () => {
        if (hasMoreLeads && !isLoadingMoreLeads) {
            fetchLeadData(adId, `after:${paginationCursors?.after}`);
        }
    };

    // Update the loadPreviousLeads function to fetch the previous page
    const loadPreviousLeads = () => {
        if (paginationCursors?.before && !isLoadingMoreLeads) {
            fetchLeadData(adId, `before:${paginationCursors?.before}`);
        }
    };

    const fetchAdInsights = async (adId) => {
        try {
            const token = authtoken; // Or however you're storing your auth token

            const insightsResponse = await getAdInsights(
                {
                    adId: adId,
                    before: "",
                    after: "",
                    adAccountId: `act_${passedData?.selectedAdAccount}`,
                    isChildAdAccount: passedData?.isChildAdAccount || false,
                },
                token
            );
            // console.log('data', insightsResponse);
            if (insightsResponse?.status === 200 && insightsResponse?.flag === true) {
                // Update the state with insights data
                // console.log("data", data);
                const insights = insightsResponse?.data?.insights?.data[0];
                const targetAudienceDetails = insightsResponse?.data?.targetingsentencelines?.targetingsentencelines;
                const adsetStartTime = insightsResponse?.data?.adset?.start_time;
                // console.log(targetAudienceDetails);
                // console.log('here2');

                // Merge the insights data with existing ad data
                const combinedData = {
                    // Basic data (from navigation state if available)
                    adName: passedData?.adName || 'Ad Campaign',
                    status: passedData?.status || 'Unknown',
                    startDate: formatDate(adsetStartTime),
                    stopDate: formatDate(insights?.date_stop),

                    // Data from insights API
                    totalBudgetUsed: isNaN(parseFloat(insights?.clicks) * parseFloat(insights?.cpc))
                        ? '0'
                        : (parseFloat(insights?.clicks) * parseFloat(insights?.cpc)).toFixed(2),
                    costPerClick: isNaN(parseFloat(insights?.cpc).toFixed(2)) ? '0' : parseFloat(insights?.cpc).toFixed(2),
                    impression: insights?.impressions || '0',
                    reach: insights?.reach || '0',
                    clicks: insights?.clicks || '0',
                    uniqueClicks: Math.round(((parseFloat(insights?.clicks) * parseFloat(insights?.cpc)) / parseFloat(insights?.cost_per_unique_click))) || '0', // Estimate if not provided
                    clickThroughRate: isNaN(parseFloat(insights?.ctr).toFixed(2)) ? '0' : parseFloat(insights?.ctr).toFixed(2),
                    uniqueLeads: insightsResponse?.data?.insightsData &&
                        insightsResponse?.data?.insightsData[0] &&
                        insightsResponse?.data?.insightsData[0]?.actions ?
                        insightsResponse?.data?.insightsData[0]?.actions?.find(
                            action => action?.action_type === "onsite_conversion.lead_grouped"
                        )?.value || '0' : '0',
                    totalLeads: insightsResponse?.data?.insightsData &&
                        insightsResponse?.data?.insightsData[0] &&
                        insightsResponse?.data?.insightsData[0]?.actions ?
                        insightsResponse?.data?.insightsData[0]?.actions?.find(
                            action => action?.action_type === "lead"
                        )?.value || '0' : '0',
                    ctwaLeads: insightsResponse?.data?.insightsData &&
                        insightsResponse?.data?.insightsData[0] &&
                        insightsResponse?.data?.insightsData[0]?.actions ?
                        insightsResponse?.data?.insightsData[0]?.actions?.find(
                            action => action?.action_type === "onsite_conversion.messaging_first_reply"
                        )?.value || '0' : '0',

                    // Additional metadata - defaults if not available
                    // dailyBudget: '150',
                    // gender: 'All',
                    ageGroup: targetAudienceDetails[4]?.content === "Age:" ? targetAudienceDetails[4]?.children : "N/A",
                    createdAt: formatDate(passedData?.startDate) || 'Unknown',
                    // fbPageName: 'Cheerio Business',
                    // caption: 'Your caption comes here!'
                };

                setAdData(combinedData);
                // console.log('here3');
                setLoading(false);

                // console.log('Ad insights loaded successfully:', insights);
            } else {
                console.error('Failed to load ad insights:', insightsResponse);
            }
        } catch (error) {
            console.error('Error fetching ad insights:', error);
        }
    };

    const handleStatusToggle = async () => {
        try {
            setStatusLoading(true);
            const newStatus = adData?.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
            if (newStatus === 'PAUSED') {
                eventsend('Pause_ad_clicked');
            } else {
                eventsend('Resume_ad_clicked');
            }
            const token = authtoken;
            const response = await changeMetaAdStatus(
                token,
                {
                    adId: adId,
                    status: newStatus,
                    isChildAdAccount: passedData?.isChildAdAccount || false,
                }
            );

            if (response?.status === 200 && response?.flag === true) {
                // Update local state with new status
                setAdData({
                    ...adData,
                    status: newStatus
                });

                console.log(`Ad status changed to ${newStatus} successfully`);
            } else {
                console.error('Failed to change ad status:', response);
            }
        } catch (error) {
            console.error('Error changing ad status:', error);
        } finally {
            setStatusLoading(false);
        }
    };

    const handleLeadTypeChange = (event) => {
        setLeadTypeFilter(event.target.value);
    };

    const handleDaysFilterChange = (event) => {
        setDaysFilter(event.target.value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getSelectedAccountName = () => {
        if (!selectedAccountId) return 'Facebook Page Name';
        const account = userAccounts?.find(acc => acc?.id === selectedAccountId);
        return account?.name || 'Facebook Page Name';
    };


    if (loading) {
        return (
            <>
                <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
                    <SpinnerLoader />
                </div>
            </>
        );
    }

    // Static chart data for demonstration
    // Add this function for chart data preparation
    const prepareChartData = (formattedLeadData) => {
        if (!formattedLeadData || formattedLeadData?.length === 0) {
            // Return some dummy data if no leads are available
            return [
                { date: '1 Mar', leads: 0 },
                { date: '5 Mar', leads: 0 },
                { date: '10 Mar', leads: 0 },
                { date: '15 Mar', leads: 0 },
                { date: '20 Mar', leads: 0 },
                { date: '25 Mar', leads: 0 },
                { date: '30 Mar', leads: 0 }
            ];
        }

        // Group leads by date
        const leadsByDate = {};
        formattedLeadData?.forEach(lead => {
            const date = new Date(lead?.createdTime);
            const dateStr = `${date?.getDate()} ${date?.toLocaleString('default', { month: 'short' })}`;

            if (!leadsByDate[dateStr]) {
                leadsByDate[dateStr] = 0;
            }
            leadsByDate[dateStr]++;
        });

        // Convert to array for chart
        return Object?.keys(leadsByDate)?.map(date => ({
            date,
            leads: leadsByDate[date]
        }));
    };

    const chartData = prepareChartData(leadData);

    // Add this function to filter leads based on selected filters
    const getFilteredLeads = () => {
        return leadData?.filter(lead => {
            // Apply lead type filter
            if (leadTypeFilter !== 'All Lead Types' && lead?.leadType !== leadTypeFilter) {
                return false;
            }

            // Apply days filter
            // const leadDate = new Date(lead?.createdTime);
            // const today = new Date();
            // const daysDiff = Math.floor((today - leadDate) / (1000 * 60 * 60 * 24));

            // if (daysFilter === '30 Days' && daysDiff > 30) return false;
            // if (daysFilter === '60 Days' && daysDiff > 60) return false;
            // if (daysFilter === '90 Days' && daysDiff > 90) return false;

            return true;
        });
    };

    return (
        <Box sx={{ bgcolor: '#f9f9f9', minHeight: '100vh', width: '100%' }}>
            {/* Header with back button and ad name */}
            <Box sx={{
                display: 'flex', alignItems: 'center', mb: 3, backgroundColor: colors.white, width: '100%', py: 1.5
            }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                    {adData?.adName || 'Ad Name'}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                    <Typography variant="body2" color="text.secondary">
                        Lifetime Data
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2}>
                {/* Main content - left side */}
                <Grid item xs={12} md={8}>
                    {/* Top metrics */}
                    <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
                        {/* Total Budget Used */}
                        <Grid item xs={6}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Total Budget Used
                                    </Typography>
                                    <Tooltip title="Total amount spent on this ad campaign" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h4" fontWeight="bold" >
                                    ₹{adData?.totalBudgetUsed
                                        ? passedData?.isChildAdAccount
                                            ? (adData.totalBudgetUsed * (1 + MARKUP_RATE)).toFixed(2)
                                            : parseFloat(adData.totalBudgetUsed).toFixed(2)
                                        : '__'
                                    }

                                </Typography>
                            </Paper>
                        </Grid>

                        {/* Cost Per Click */}
                        <Grid item xs={6}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Cost Per Lead
                                    </Typography>
                                    <Tooltip title="Average cost paid for each lead on your ad" arrow placement="top">
                                        <InfoOutlineIcon sx={{ ml: 0.5, mb: 2, fontSize: 16, color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h4" fontWeight="bold">
                                    ₹{adData?.totalLeads > 0 && adData?.totalBudgetUsed
                                        ? passedData?.isChildAdAccount
                                            ? ((adData.totalBudgetUsed * (1 + MARKUP_RATE)) / adData.totalLeads).toFixed(2)
                                            : (adData.totalBudgetUsed / adData.totalLeads).toFixed(2)
                                        : '0'
                                    }
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Middle metrics */}
                    <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
                        {/* Impression */}
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Total Leads
                                    </Typography>
                                    <Tooltip title="Total number of leads generated from this ad campaign" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.totalLeads || '__'}
                                </Typography>
                            </Paper>
                        </Grid>

                        {/* Reach */}
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Unique Leads
                                    </Typography>
                                    <Tooltip title="Number of distinct individuals who have become leads" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.uniqueLeads || '__'}
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        CTWA Leads
                                    </Typography>
                                    <Tooltip title="Total number of CTWA leads generated from this ad campaign" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.ctwaLeads || '__'}
                                </Typography>
                            </Paper>
                        </Grid>
                        {/* Clicks */}
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Clickthrough Rate
                                    </Typography>
                                    <Tooltip title="Percentage of impressions that resulted in a click" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.clickThroughRate || '__'}%
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Bottom metrics */}
                    <Grid container spacing={2} sx={{ mb: 4, px: 2 }}>
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Impressions
                                    </Typography>
                                    <Tooltip title="Total number of times your ad was displayed" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.impression || '__'}
                                </Typography>
                            </Paper>
                        </Grid>
                        {/* Click-through rate */}
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Reach
                                    </Typography>
                                    <Tooltip title="Number of unique users who saw your ad" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.reach || '__'}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Clicks
                                    </Typography>
                                    <Tooltip title="Total number of clicks received on your ad" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.clicks || '__'}
                                </Typography>
                            </Paper>
                        </Grid>
                        {/* Messages */}
                        {/* <Grid item xs={3}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Messages
                                    </Typography>
                                    <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000' }} />
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData.messages || '49'}
                                </Typography>
                            </Paper>
                        </Grid> */}

                        {/* Unique Leads */}


                        {/* Engagement Rate */}
                        {/* <Grid item xs={3}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Engagement Rate
                                    </Typography>
                                    <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000' }} />
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData.engagementRate || '9'}%
                                </Typography>
                            </Paper>
                        </Grid> */}
                        {/* Unique Clicks */}
                        <Grid item xs={3}>
                            <Paper sx={{
                                p: 2, borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                                boxShadow: 'none',
                            }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="black" sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: 2 }}>
                                        Unique Clicks
                                    </Typography>
                                    <Tooltip title="Number of distinct users who clicked on your ad" arrow placement="top">
                                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, mb: 2, fontSize: '16px', color: '#000000', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {adData?.uniqueClicks || '__'}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>






                </Grid>

                {/* Right sidebar - Ad Details */}
                <Grid item xs={12} md={4} sx={{ px: 2 }}>
                    <Paper sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                        mb: 2,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}>
                            <Typography variant="h6" fontWeight="bold">
                                Ad Details
                            </Typography>
                            <Box
                                component="button"
                                onClick={handleStatusToggle}
                                disabled={statusLoading}
                                sx={{
                                    width: 36,
                                    height: 20,
                                    bgcolor: adData?.status === 'PAUSED' ? '#E4E4E7' : '#22C55E',
                                    borderRadius: 10,
                                    position: 'relative',
                                    border: 'none',
                                    cursor: 'pointer',
                                    opacity: statusLoading ? 0.5 : 1,
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: '#FFFFFF',
                                        top: '50%',
                                        transform: adData?.status === 'PAUSED' ? 'translate(0%, -50%)' : 'translate(100%, -50%)',
                                        left: 2,
                                        transition: 'transform 0.2s'
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{ borderBottom: '1px solid #E5E7EB', mb: 3 }} />

                        {/* Status */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Status
                            </Typography>
                            <Chip
                                label={adData?.status || 'PAUSED'}
                                size="small"
                                sx={{
                                    bgcolor: adData?.status === 'ACTIVE' ? '#DEF7EC' : '#FEF3C7',
                                    color: adData?.status === 'ACTIVE' ? '#03543F' : '#D97706',
                                    borderRadius: 1,
                                    fontWeight: 500
                                }}
                            />
                        </Box>

                        {/* Ad Creative Preview */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Ad Creative Preview
                            </Typography>
                            {adPreviewData ? (
                                <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    sx={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        color: colors.primary03,
                                        '&:hover': {
                                            color: colors.primary03
                                        }
                                    }}
                                    onClick={handlePreviewClick}
                                >
                                    See Preview
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>
                                    N/A
                                </Typography>
                            )}
                        </Box>

                        {/* Age Group */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 2
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Age Group
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {adData.ageGroup || '18 - 65'}
                            </Typography>
                        </Box>

                        {/* Created At */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 2
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Created At
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {adData?.createdAt || 'Unknown'}
                            </Typography>
                        </Box>

                        {/* Started At */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Started At
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {adData?.startDate || 'Unknown'}
                            </Typography>
                        </Box>
                    </Paper>
                    <Modal
                        open={previewModalOpen}
                        onClose={handlePreviewClose}
                        aria-labelledby="ad-preview-modal"
                        aria-describedby="ad-preview-description"
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: '80%', md: '60%', lg: '40%' },
                            maxWidth: 400,
                            maxHeight: '90vh',
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                            boxShadow: 24,
                        }}>
                            {/* Modal Header */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 2,
                            }}>
                                <Typography variant="h6" fontWeight={600} color="#000000">
                                    Ad Preview
                                </Typography>
                                <Box
                                    component="button"
                                    onClick={handlePreviewClose}
                                    sx={{
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        fontSize: 20,
                                        fontWeight: 'bold',
                                        color: '#666666',
                                        padding: 0,
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={images.CROSS_BLACK}
                                        alt="Close"
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            objectFit: 'contain',
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Modal Content with Padding */}
                            <Box sx={{ p: 3 }}>
                                <Paper
                                    sx={{
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        border: '1px solid #D9D9D9'
                                    }}
                                >
                                    {/* Facebook Post Header */}
                                    <Box sx={{ p: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            {/* Profile Section */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* Profile Picture Placeholder */}
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        bgcolor: '#E0E0E0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 14, color: '#666' }}>
                                                        FB
                                                    </Typography>
                                                </Box>

                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: 14,
                                                            color: '#000000',
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {getSelectedAccountName()}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: '#666666',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        Sponsored
                                                        <CiGlobe />
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Three Dots Menu */}
                                            <Box
                                                sx={{
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '50%',
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 18, color: '#666666' }}>
                                                    ⋯
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Caption */}
                                        <Typography
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: 16,
                                                color: '#000000',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.4,
                                                textAlign: 'left',
                                                mb: 1,
                                            }}
                                        >
                                            {adPreviewData?.adCaption || 'Your caption comes here!'}
                                        </Typography>
                                    </Box>

                                    {/* Rest of your existing media and content sections remain the same */}
                                    {/* Media Section */}
                                    {mediaType === 'Video' ? (
                                        // Your existing video code
                                        s3Link ? (
                                            <video
                                                src={s3Link}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    objectFit: 'contain',
                                                }}
                                                controls
                                                poster={thumbnailS3Link || undefined}
                                                onLoadedMetadata={(e) => {
                                                    const video = e.target;
                                                    setMediaDimensions({ width: video.videoWidth, height: video.videoHeight });
                                                    setIsMediaLoaded(true);
                                                }}
                                            />
                                        ) : (
                                            // Your existing video fallback code
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: calculateAdaptiveHeight(),
                                                    bgcolor: '#D9D9D9',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {thumbnailS3Link ? (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundImage: `url(${thumbnailS3Link})`,
                                                            backgroundSize: 'contain',
                                                            backgroundPosition: 'center',
                                                            backgroundRepeat: 'no-repeat',
                                                        }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={images.metaAdsVideoPreview}
                                                        alt="Video Placeholder"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            backgroundColor: '#D9D9D9',
                                                            display: 'block',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )
                                    ) : (
                                        // Your existing image code remains the same
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                bgcolor: '#D9D9D9',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {s3Link ? (
                                                <Box
                                                    component="img"
                                                    src={s3Link}
                                                    alt="Uploaded Image"
                                                    sx={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                    onLoad={(e) => {
                                                        const img = e.target;
                                                        setMediaDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                                                        setIsMediaLoaded(true);
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={images.metaAdsImagePreview}
                                                    alt="Image Placeholder"
                                                    style={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}

                                    {/* WhatsApp CTA - Keep your existing code */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            backgroundColor: '#F7F8FA',
                                            p: 1.5,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 400, fontSize: 14, color: '#000000' }}
                                            >
                                                Chat on WhatsApp
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 700, fontSize: 14 }}
                                            >
                                                {adPreviewData?.headline || 'Footer Text'}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: '#E2E5EC',
                                                borderRadius: 1,
                                                px: 2,
                                                py: 1,
                                            }}
                                        >
                                            <FaWhatsapp style={{ fontSize: 16, marginRight: 6 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14 }}>
                                                WhatsApp
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Like/Comment/Share - Keep your existing code */}
                                    <Box
                                        sx={{
                                            borderTop: '1px solid #E0E0E0',
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            alignItems: 'center',
                                            py: 1,
                                            px: 2,
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        {['Like', 'Comment', 'Share'].map((label) => {
                                            const icons = {
                                                Like: <AiOutlineLike style={{ fontSize: 18 }} />,
                                                Comment: <FaRegComment style={{ fontSize: 16 }} />,
                                                Share: <PiShareFat style={{ fontSize: 18 }} />,
                                            };

                                            return (
                                                <Box
                                                    key={label}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {icons[label]}
                                                    <Typography variant="body2" sx={{ fontSize: 14, color: '#000000' }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    </Modal>

                    {/* <Paper
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{ fontWeight: 600, fontSize: 14, color: '#000000', lineHeight: 1.2 }}
                                    >
                                        {adPreviewData?.metaPageId ? 'Facebook Page' : 'Facebook Page Name'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: '#666666',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        Sponsored
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: '#4267B2',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography sx={{ color: 'white', fontSize: 8, fontWeight: 'bold' }}>
                                                i
                                            </Typography>
                                        </Box>
                                    </Typography>
                                </Box>

                                <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#666666' }}>
                                    ⋮
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: 16,
                                    color: '#000000',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.4,
                                    textAlign: 'left',
                                    mb: 1,
                                }}
                            >
                                {adPreviewData?.adCaption || 'Your caption comes here!'}
                            </Typography>
                        </Box>

                        {mediaType === 'Video' ? (
                            s3Link ? (
                                <video
                                    src={s3Link}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        objectFit: 'contain',
                                    }}
                                    controls
                                    poster={thumbnailS3Link || undefined}
                                    onLoadedMetadata={(e) => {
                                        const video = e.target;
                                        setMediaDimensions({ width: video.videoWidth, height: video.videoHeight });
                                        setIsMediaLoaded(true);
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: calculateAdaptiveHeight(),
                                        bgcolor: '#D9D9D9',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    {thumbnailS3Link ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                backgroundImage: `url(${thumbnailS3Link})`,
                                                backgroundSize: 'contain',
                                                backgroundPosition: 'center',
                                                backgroundRepeat: 'no-repeat',
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={images.metaAdsVideoPreview}
                                            alt="Video Placeholder"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                backgroundColor: '#D9D9D9',
                                                display: 'block',
                                            }}
                                        />
                                    )}
                                </Box>
                            )
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    bgcolor: '#D9D9D9',
                                    overflow: 'hidden',
                                }}
                            >
                                {s3Link ? (
                                    <Box
                                        component="img"
                                        src={s3Link}
                                        alt="Uploaded Image"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                        onLoad={(e) => {
                                            const img = e.target;
                                            setMediaDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                                            setIsMediaLoaded(true);
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={images.metaAdsImagePreview}
                                        alt="Image Placeholder"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                )}
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#F7F8FA',
                                p: 1.5,
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400, fontSize: 14, color: '#000000' }}
                                >
                                    Chat on WhatsApp
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, fontSize: 14 }}
                                >
                                    {adPreviewData?.headline || 'Footer Text'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#E2E5EC',
                                    borderRadius: 1,
                                    px: 2,
                                    py: 1,
                                }}
                            >
                                <FaWhatsapp style={{ fontSize: 16, marginRight: 6 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14 }}>
                                    WhatsApp
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                borderTop: '1px solid #E0E0E0',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                py: 1,
                                px: 2,
                                backgroundColor: '#fff',
                            }}
                        >
                            {['Like', 'Comment', 'Share'].map((label) => {
                                const icons = {
                                    Like: <AiOutlineLike style={{ fontSize: 18 }} />,
                                    Comment: <FaRegComment style={{ fontSize: 16 }} />,
                                    Share: <PiShareFat style={{ fontSize: 18 }} />,
                                };

                                return (
                                    <Box
                                        key={label}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {icons[label]}
                                        <Typography variant="body2" sx={{ fontSize: 14, color: '#000000' }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper> */}

                    {/* Ad preview */}
                    {/* <Paper sx={{
                        p: 3,
                        borderRadius: 2,
                        mt: 2,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
                    }} >
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                                {adData.fbPageName || 'Facebook Page Name'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                Sponsored
                                <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                                    <img src={images.InfoIcon} alt="Info" width={16} height={16} />
                                </Box>
                            </Typography>
                            <Typography variant="body2" sx={{ my: 1 }}>
                                {adData.caption || 'Your caption comes here!'}
                            </Typography>

                            <Box
                                sx={{
                                    width: '100%',
                                    height: '150px',
                                    bgcolor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    mb: 1
                                }}
                            >
                                <img src={images.ImageIcon} alt="Ad preview" width={24} height={24} />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2">
                                    WhatsApp
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '16px',
                                        px: 1.5,
                                        py: 0.5
                                    }}
                                >
                                    <img src={images.WhatsAppIcon} alt="WhatsApp" width={16} height={16} sx={{ mr: 0.5 }} />
                                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                                        WhatsApp
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2">
                                Footer Text
                            </Typography>
                        </Box>
                    </Paper> */}
                </Grid>
            </Grid>
            {/* Leads Generated Chart */}
            {/* <Paper sx={{ p: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight="bold">
                            Leads Generated
                        </Typography>
                        <InfoOutlineIcon fontSize="small" sx={{ ml: 0.5, fontSize: '16px', color: '#000000' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Per day
                    </Typography>
                </Box>

                <Box sx={{ height: 300, width: '100%' }}>
                    <LineChart
                        xAxis={[
                            {
                                data: chartData.map(item => item.date),
                                scaleType: 'point',
                            },
                        ]}
                        series={[
                            {
                                data: chartData.map(item => item.leads),
                                label: 'Leads',
                                color: '#7C3AED',
                                curve: 'linear',   // straight lines
                                area: true,        // enable area fill
                            },
                        ]}
                        // sx={{
                        //     '& .MuiLineElement-root': {
                        //         strokeWidth: 2,
                        //     },
                        //     '& .MuiMarkElement-root': {
                        //         stroke: '#7C3AED',
                        //         strokeWidth: 2,
                        //         fill: '#7C3AED',
                        //         r: 4,
                        //     },
                        //     '& .MuiAreaElement-series-Leads': {
                        //         fill: 'url(#purpleGradient)',
                        //     },
                        // }}
                        slotProps={{
                            legend: { hidden: true },
                        }}
                    >
                        <defs>
                                    <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.8} />
                                        <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.2} />
                                    </linearGradient>
                                </defs>
                    </LineChart>
                </Box>
            </Paper> */}
            {/* All Leads Table */}
            <Grid sx={{ px: 2 }} >
                <Paper sx={{ p: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                            All Leads
                        </Typography>
                        {/* <Box sx={{ display: 'flex', gap: 1 }}>
                        <Select
                            value={leadTypeFilter}
                            onChange={handleLeadTypeChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="All Lead Types">All Lead Types</MenuItem>
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Contacted">Contacted</MenuItem>
                        </Select>
                        <Select
                            value={daysFilter}
                            onChange={handleDaysFilterChange}
                            size="small"
                            sx={{ minWidth: 100 }}
                        >
                            <MenuItem value="30 Days">30 Days</MenuItem>
                            <MenuItem value="60 Days">60 Days</MenuItem>
                            <MenuItem value="90 Days">90 Days</MenuItem>
                        </Select>
                    </Box> */}
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell>User Name</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Sent At</TableCell>
                                    {/* <TableCell>Lead Type</TableCell>
                <TableCell>Messages</TableCell> */}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingMoreLeads ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%'
                                            }}>
                                                <SpinnerLoader />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : leadData?.length > 0 ? (
                                    leadData?.map((lead) => (
                                        <TableRow key={lead?.id}>
                                            <TableCell>{lead?.name || "N/A"}</TableCell>
                                            <TableCell>{lead?.phone || "N/A"}</TableCell>
                                            <TableCell>{lead?.email || "N/A"}</TableCell>
                                            <TableCell>{lead?.sentAt || "N/A"}</TableCell>
                                            {/* <TableCell>
                            <Chip
                                label={lead.leadType}
                                size="small"
                                sx={{
                                    bgcolor: lead.leadType === 'New' ? '#DCFCE7' : '#F3E8FF',
                                    color: lead.leadType === 'New' ? '#16A34A' : '#7C3AED',
                                    borderRadius: '4px',
                                }}
                            />
                        </TableCell>
                        <TableCell>{lead.messages}</TableCell> */}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No leads data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                        <Button
                            sx={{ color: colors.primary03 }}
                            disabled={!paginationCursors?.hasPrevious || isLoadingMoreLeads}
                            onClick={loadPreviousLeads}
                            startIcon={
                                <ArrowBackIosNewIcon
                                    sx={{
                                        color: !paginationCursors?.hasPrevious || isLoadingMoreLeads ? 'grey.400' : colors.primary03
                                    }}
                                />
                            }
                        >
                            Previous
                        </Button>

                        <Button
                            sx={{ color: colors.primary03 }}
                            disabled={!hasMoreLeads || isLoadingMoreLeads}
                            onClick={loadMoreLeads}
                            endIcon={
                                <ArrowForwardIosIcon
                                    sx={{
                                        color: !hasMoreLeads || isLoadingMoreLeads ? 'grey.400' : colors.primary03
                                    }}
                                />
                            }
                        >
                            Next
                        </Button>
                    </Box>
                </Paper>
            </Grid>
        </Box >
    );
};

export default AdDetailAnalytics;